import {Party, Individual} from '@amicamutual/amica-common';
import {pluck} from 'ramda';
import {createSelector} from 'reselect';

import {RootState} from 'ducks';
import {State as ProfileState} from 'ducks/Profile';
import {AMICA_RESPONSE_DETAIL, OUTAGE_CODES} from 'enums/ResponseHeaders';
import {isDefined} from 'utils/data-operations';

/**
 * Selector for the Profile redux state.
 */
const getProfileState = (state: RootState): ProfileState => state.profile;

/**
 * Selector for the Party data.
 */
export const getParty = createSelector(getProfileState, (profileState: ProfileState) => profileState.party);

/**
 * Selector to take Party data and return only required Indvidual fields.
 */
export const getIndividualFromParty = createSelector(
  getParty,
  (party: Party): Individual => ({
    title: party.title,
    firstName: party.firstName,
    lastName: party.lastName,
    uuid: party.uuid
  })
);

/**
 * Selector that returns a boolean depending on whether or not the Party data is loaded.
 */
export const isPartyLoaded = createSelector(getParty, (party: Party | null | undefined): boolean => !!party);

/**
 * Selector that returns a boolean depending on whether or not the Party data is loading
 */
export const isLoadingProfile = createSelector(
  getProfileState,
  (profileState: ProfileState): boolean => profileState.isLoadingProfile
);

/**
 * Selector for the first name of the Party.
 */
export const getFirstName = createSelector(getParty, (party) => (!!party ? party.firstName : undefined));

/**
 * Selector for the last name of the Party.
 */
export const getLastName = createSelector(getParty, (party) => (!!party ? party.lastName : undefined));

/**
 * Selector for the full name of the Party.
 */
export const getFullName = createSelector(getParty, (party) =>
  !!party ? `${party.firstName} ${party.lastName}` : undefined
);

/**
 * Selector for the dob of the Party.
 */
export const getDateOfBirth = createSelector(getParty, (party) => (!!party ? party.dateOfBirth : undefined));

/**
 * Selector for the webIdentity of the Party.
 */
export const getWebIdentity = createSelector(getParty, (party) => (!!party ? party.webIdentity : undefined));

/**
 * Selector for userId from webIdentity.
 */
export const getUserId = createSelector(getWebIdentity, (webIdentity) =>
  !!webIdentity ? webIdentity.userId : undefined
);

/**
 * Selector to retrieve the roles from the webIdentity.
 */
export const getWebIdentityRoles = createSelector(getWebIdentity, (webIdentity) => webIdentity?.roles);

/**
 * Selector for the lastLoginDate of the Party.
 */
export const getLastLoginDate = createSelector(
  getProfileState,
  (profileState: ProfileState) => profileState.lastLoginDate
);

export const getCurrentLoginDate = createSelector(
  getProfileState,
  (profileState: ProfileState) => profileState.currentLoginDate
);

/**
 * There was an error fetching the party.
 */
export const hasPartyError = createSelector(
  getProfileState,
  (profileState: ProfileState) => !!profileState.errorLoadingProfile
);

/**
 * Selector for the party UUID from Okta stored in Profile
 */
export const getPartyUUID = createSelector(getProfileState, (profileState: ProfileState) => profileState.partyUUID);

/**
 * Selector for the Amica account numbers associated with this Profile
 */
export const getAccountNumbers = createSelector(getProfileState, (profileState: ProfileState) =>
  pluck('number', profileState.accounts)
);

export const getFirstInsuredYear = createSelector(getProfileState, (profileState: ProfileState) => {
  const allAvailableYears = pluck('firstInsuredYear', profileState.accounts).filter(isDefined);

  return allAvailableYears.length > 0 ? Math.min(...allAvailableYears) : undefined;
});

const hasOutageOccured = (outageCode: string) =>
  createSelector(getProfileState, (profileState: ProfileState) => {
    const amicaResponseDetail = profileState.payloadHeader?.[AMICA_RESPONSE_DETAIL];

    return !!amicaResponseDetail?.includes(outageCode);
  });

export const hasLifeOutageOccured = hasOutageOccured(OUTAGE_CODES.LIFE);

export const hasAccountOutageOccured = hasOutageOccured(OUTAGE_CODES.ACCOUNT);

export const getOnboardingCompleted = createSelector(
  getProfileState,
  (state: ProfileState) => state.onboardingCompleted
);
